import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";

import { BehaviorSubject } from "rxjs";

import { AddGeoJsonLocationsModel as Model } from "@components/locations/add-geojson-locations/add-geojson-locations.model";
import { BulkAddLocationsModel } from "@components/locations/bulk-add-locations/bulk-add-locations.component.model";
import { BulkAddLocationsService } from "@components/locations/bulk-add-locations/bulk-add-locations.service";
import { BulkAddLocationsEnterRecordsModel } from "@components/locations/bulk-add-locations/enter-records/bulk-add-locations-enter-records.model";
import { FileWrapper } from "@shared/classes/file-wrapper.class";
import { CommonConstants, TextConstants } from "@shared/constants";
import { IGeoLocation } from "@shared/interfaces";
import { FileUtils } from "@shared/utils";
import { GeoJSONUtils } from "@shared/utils/geojson-utils";

@Component({
  standalone: false,
  selector: "app-add-geojson-locations",
  templateUrl: "./add-geojson-locations.component.html",
  styleUrls: ["./add-geojson-locations.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddGeoJsonLocationsComponent {
  readonly maxFileSizeInMb: number = 1;

  readonly supportedFileTypes: string[] = ["JSON", "GEOJSON"];

  readonly maxNumberOfFiles: number = BulkAddLocationsEnterRecordsModel.maxRecordsCount;

  private locations: BulkAddLocationsModel.LocationFromGeoData[] = [];

  private readonly bulkAddLocationsService: BulkAddLocationsService =
    inject(BulkAddLocationsService);

  invalidFiles = new BehaviorSubject<Model.InvalidFile[]>([]);

  hasValidLocations = new BehaviorSubject<boolean>(false);

  private readonly allowedFileExtensionsText =
    CommonConstants.GEOJSON_UPLOAD_ALLOWED_FILE_EXTENSIONS.map((e) => `.${e.toLowerCase()}`).join(
      " or ",
    );

  readonly translations = {
    uploadBoxFooterLeftText: $localize`:@@bulk-add-locations.geojson.uploadBoxFooterLeftText:Drop GeoJSON files here`,
    uploadBoxFooterRightText: $localize`:@@bulk-add-locations.geojson.uploadBoxFooterRightText:or click to select`,
    fileInfo: $localize`:@@bulk-add-locations.geojson.fileInfo:(Supported formats: ${this.allowedFileExtensionsText}; Max file size ${CommonConstants.MAX_GEO_JSON_SIZE_IN_MB}:maxSizeInMb:MB)`,
    cancel: TextConstants.CANCEL,
  };

  private readonly BYTES_PER_KB = 1024;

  private readonly BYTES_PER_MB = this.BYTES_PER_KB * 1024;

  private readonly KB_THRESHOLD = 0.1 * this.BYTES_PER_MB;

  constructor(public dialogRef: MatDialogRef<AddGeoJsonLocationsComponent>) {}

  onFilesSelected(fileWrappers: FileWrapper[]): void {
    this.locations = [];
    this.hasValidLocations.next(false);

    const errors: Model.InvalidFile[] = [];
    const validFiles: FileWrapper[] = [];

    for (const wrapper of fileWrappers) {
      if (wrapper.validity && !wrapper.validity.isValid) {
        let errorMessage = FileUtils.errors[FileUtils.FileErrorCodeEnum.supportedFileTypes];

        if (wrapper.validity.errorType === FileUtils.FileErrorCodeEnum.maxSizeInMb) {
          errorMessage = FileUtils.errors[FileUtils.FileErrorCodeEnum.maxSizeInMb];
        }

        errors.push({
          fileName: wrapper.file.name,
          error: errorMessage,
          size: this.getFileSize(wrapper.file),
        });
      } else {
        validFiles.push(wrapper);
      }
    }

    this.invalidFiles.next(errors);

    let processedFiles: number = 0;
    const totalValidFiles: number = validFiles.length;

    for (const wrapper of validFiles) {
      this.readFile(wrapper.file, (error?: string) => {
        processedFiles++;

        if (error) {
          errors.push({
            fileName: wrapper.file.name,
            error,
            size: this.getFileSize(wrapper.file),
          });

          this.invalidFiles.next(errors);
        } else {
          this.hasValidLocations.next(true);
        }

        if (processedFiles === totalValidFiles && !errors.length) {
          this.bulkAddLocationsService.openSlideOver(this.locations);
          this.onClose();
        }
      });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onClickSkipFiles(): void {
    this.bulkAddLocationsService.openSlideOver(this.locations);
    this.onClose();
  }

  private getFileSize(file: File): string {
    if (file.size < this.KB_THRESHOLD) {
      return `${(file.size / this.BYTES_PER_KB).toFixed(3)} KB`;
    }

    return `${(file.size / this.BYTES_PER_MB).toFixed(1)} MB`;
  }

  private readFile(file: File, callback: (error?: string) => void): void {
    const reader = new FileReader();

    reader.onload = () => {
      const geoJsonData = JSON.parse(reader.result as string) as IGeoLocation;

      if (!GeoJSONUtils.isValid(geoJsonData)) {
        const errorMessage = GeoJSONUtils.getGeoJsonError(geoJsonData);

        callback(errorMessage);

        return;
      }

      try {
        const location: BulkAddLocationsModel.LocationFromGeoData = {
          name:
            geoJsonData.features[0].properties.name ||
            FileUtils.getFileNameWithoutExtension(file.name),
          geoLocationWithFileName: {
            geoLocation: geoJsonData,
            fileName: file.name,
          },
        };

        this.locations.push(location);
        callback();
      } catch {
        callback($localize`:@@file.error.reading:Error reading file.`);
      }
    };

    reader.onerror = () => {
      callback($localize`:@@file.error.reading:Error reading file.`);
    };

    reader.readAsText(file);
  }
}
